import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Stack,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import { db } from "../lib/firebase";
import { api } from "../lib/api";

export interface HallMedia {
  id: string;
  url: string;
  thumbUrl?: string;
  order: number;
}

/**
 * Gallery manager for one hall. The images themselves live in Storage; this
 * edits the `media` subcollection through the API, which owns the ordering and
 * the cover. The first image by `order` is the cover — the API re-packs both
 * on delete, so nothing here needs to compute them.
 */
export function HallPhotosDialog({
  orgId,
  hallId,
  hallName,
  open,
  onClose,
  onUpload,
  uploading,
}: {
  orgId: string;
  hallId: string | null;
  hallName: string;
  open: boolean;
  onClose: () => void;
  onUpload: () => void;
  uploading: boolean;
}) {
  const [media, setMedia] = useState<HallMedia[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !hallId) return;
    setMedia(null);
    setError(null);
    const q = query(collection(db, "halls", hallId, "media"), orderBy("order"));
    return onSnapshot(
      q,
      (snap) => setMedia(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as HallMedia)),
      // Without this an unreadable gallery renders as an empty one, and the
      // owner concludes their photos were lost.
      () => setError("عکس‌ها بارگذاری نشد"),
    );
  }, [open, hallId]);

  async function remove(mediaId: string) {
    if (!hallId) return;
    setDeletingId(mediaId);
    setError(null);
    try {
      await api(`/orgs/${orgId}/halls/${hallId}/media/${mediaId}`, { method: "DELETE" });
      setConfirmId(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "حذف عکس ناموفق بود");
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>عکس‌های {hallName}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" mb={2}>
          نخستین عکس، عکس اصلی تالار است و در نتایج جستجو دیده می‌شود. با حذف آن، عکس بعدی جایش را
          می‌گیرد.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {media === null && !error && (
          <Stack alignItems="center" py={4}>
            <CircularProgress />
          </Stack>
        )}

        {media?.length === 0 && (
          <Typography color="text.secondary" textAlign="center" py={4}>
            هنوز عکسی اضافه نشده — بدون عکس، تالار در نتایج جستجو جای خالی نشان می‌دهد.
          </Typography>
        )}

        {media && media.length > 0 && (
          <ImageList cols={3} gap={12} sx={{ m: 0 }}>
            {media.map((item, index) => (
              <ImageListItem key={item.id} sx={{ borderRadius: 2, overflow: "hidden" }}>
                <Box
                  component="img"
                  src={item.thumbUrl || item.url}
                  alt=""
                  loading="lazy"
                  sx={{ width: "100%", height: 160, objectFit: "cover", display: "block" }}
                />
                <ImageListItemBar
                  position="top"
                  sx={{ background: "transparent" }}
                  actionPosition="left"
                  actionIcon={
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      sx={{ width: "100%", p: 0.5 }}
                    >
                      {index === 0 ? (
                        <Chip size="small" color="primary" label="عکس اصلی" />
                      ) : (
                        <span />
                      )}
                      <IconButton
                        size="small"
                        aria-label="حذف عکس"
                        disabled={deletingId === item.id}
                        onClick={() => setConfirmId(item.id)}
                        sx={{ bgcolor: "rgba(0,0,0,0.55)", color: "#fff" }}
                      >
                        {deletingId === item.id ? (
                          <CircularProgress size={18} sx={{ color: "#fff" }} />
                        ) : (
                          <DeleteIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Stack>
                  }
                />
              </ImageListItem>
            ))}
          </ImageList>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onUpload} disabled={uploading}>
          {uploading ? "در حال آپلود…" : "+ افزودن عکس"}
        </Button>
        <Box flex={1} />
        <Button onClick={onClose}>بستن</Button>
      </DialogActions>

      {/* Deleting the cover changes what every customer sees first — confirm it. */}
      <Dialog open={confirmId !== null} onClose={() => setConfirmId(null)}>
        <DialogTitle>این عکس حذف شود؟</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {media && confirmId === media[0]?.id
              ? "این عکس اصلی تالار است. با حذف آن، عکس بعدی جایش را می‌گیرد."
              : "این کار برگشت‌پذیر نیست."}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmId(null)}>انصراف</Button>
          <Button
            color="error"
            variant="contained"
            disabled={deletingId !== null}
            onClick={() => confirmId && remove(confirmId)}
          >
            حذف
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
